/**
 * BaseModal.Media - Media Plugin for BaseModal
 * Handles images, videos, and iframes with gallery support
 *
 * Features:
 * - Image lightbox with lazy loading
 * - Video player (HTML5 video)
 * - Iframe embeds (YouTube, Vimeo, VK, Rutube)
 * - Gallery navigation (prev/next)
 * - Keyboard shortcuts (arrow keys, ESC)
 * - Touch gestures support
 *
 * @requires BaseModal.js
 * @version 1.0.0
 * @author Your Team
 */

'use strict';

// ========================================
// CONSTANTS & DEFAULTS
// ========================================

/**
 * Default configuration for media modal
 * @type {Object}
 */
const DEFAULTS = {
    selector: '[data-basemodal-media]',
    gallery: true,
    autoplay: false,
    loop: false,
    controls: true,
    preload: 'metadata',
    loading: 'lazy',
    theme: 'dark',
    animation: 'zoom',
    closeOnVideoEnd: false,
    fullscreen: false,  // Show fullscreen button
    debugMode: false,
    locales: {
        close: 'Close',
        fullscreen: 'Toggle Fullscreen',
        prev: 'Previous',
        next: 'Next',
        unsupported: 'Unsupported media type'
    }
};

/**
 * Regular expressions for media type detection
 * @type {Object}
 */
const PATTERNS = {
    image: /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|avif)$/i,
    video: /\.(mp4|webm|ogg|mov|avi)$/i,
    youtube: /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i,
    vimeo: /vimeo\.com\/(?:video\/)?(\d+)/i,
    vkvideo: /vkvideo\.ru|vk\.com\/video/i,
    rutube: /rutube\.ru/i
};

// ========================================
// MAIN CLASS
// ========================================

/**
 * BaseModal.Media Class
 * Manages media modals with gallery support
 */
class BaseModalMedia {
    /**
     * Create a media modal instance
     * @param {Object} config - Configuration options
     * @param {string} config.selector - CSS selector for media elements
     * @param {boolean} config.gallery - Enable gallery mode
     * @param {boolean} config.autoplay - Autoplay videos
     * @param {boolean} config.loop - Loop videos
     * @param {boolean} config.controls - Show video controls
     * @param {string} config.theme - Modal theme (light|dark)
     * @param {string} config.animation - Animation type
     * @param {boolean} config.fullscreen - Show fullscreen button (default: false)
     * @param {boolean} config.debugMode - Enable debug logging
     */
    constructor(config = {}) {
        this.config = { ...DEFAULTS, ...config };
        this.galleries = {};
        this.currentGallery = null;
        this.currentIndex = 0;
        this.currentModal = null;
        this.mediaElements = [];
        this.isTransitioning = false;  // Flag to prevent multiple transitions
        this.transitionTimeout = null;  // Store timeout for cleanup

        if (this.config.debugMode) {
            console.log('BaseModal.Media initialized with config:', this.config);
        }
    }

    // ========================================
    // INITIALIZATION
    // ========================================

    /**
     * Initialize media plugin
     * Collects all media elements and attaches event listeners
     * @public
     */
    init() {
        this.collectMediaElements();
        this.attachEventListeners();

        if (this.config.debugMode) {
            console.log('BaseModal.Media: Found', this.mediaElements.length, 'media elements');
            console.log('Galleries:', Object.keys(this.galleries));
        }
    }

    /**
     * Collect all media elements from DOM
     * Groups elements by gallery attribute
     * @private
     */
    collectMediaElements() {
        const elements = document.querySelectorAll(this.config.selector);

        elements.forEach((element, index) => {
            const mediaData = this.extractMediaData(element, index);
            this.mediaElements.push(mediaData);

            // Group by gallery
            if (this.config.gallery && mediaData.gallery) {
                if (!this.galleries[mediaData.gallery]) {
                    this.galleries[mediaData.gallery] = [];
                }
                mediaData.galleryIndex = this.galleries[mediaData.gallery].length;
                this.galleries[mediaData.gallery].push(mediaData);
            }
        });
    }

    /**
     * Extract media data from DOM element
     * @param {HTMLElement} element - Media link element
     * @param {number} index - Element index
     * @returns {Object} Media data object
     * @private
     */
    extractMediaData(element, index) {
        const url = element.getAttribute('href') || element.dataset.src;
        const type = element.dataset.type || this.detectMediaType(url);

        return {
            element,
            url,
            type,
            title: element.dataset.title || element.getAttribute('title') || '',
            description: element.dataset.description || '',
            gallery: element.dataset.gallery || null,
            galleryIndex: null,
            index,
            // Video specific
            autoplay: element.dataset.autoplay === 'true' || this.config.autoplay,
            loop: element.dataset.loop === 'true' || this.config.loop,
            // Iframe specific
            iframeWidth: element.dataset.iframeWidth || 853,
            iframeHeight: element.dataset.iframeHeight || 480,
            iframeAllow: element.dataset.iframeAllow || 'autoplay; fullscreen; picture-in-picture',
            // Config overrides
            theme: element.dataset.theme || null,
            size: element.dataset.size || null,
            animation: element.dataset.animation || null
        };
    }

    // ========================================
    // MEDIA TYPE DETECTION
    // ========================================

    /**
     * Detect media type from URL
     * @param {string} url - Media URL
     * @returns {string} Media type (image|video|iframe)
     * @private
     */
    detectMediaType(url) {
        if (PATTERNS.image.test(url)) return 'image';
        if (PATTERNS.video.test(url)) return 'video';
        if (PATTERNS.youtube.test(url)) return 'iframe';
        if (PATTERNS.vimeo.test(url)) return 'iframe';
        if (PATTERNS.vkvideo.test(url)) return 'iframe';
        if (PATTERNS.rutube.test(url)) return 'iframe';

        // Default to iframe for unknown URLs
        return 'iframe';
    }

    // ========================================
    // EVENT HANDLING
    // ========================================

    /**
     * Attach click event listeners to media elements
     * @private
     */
    attachEventListeners() {
        this.mediaElements.forEach(mediaData => {
            mediaData.element.addEventListener('click', (e) => {
                e.preventDefault();
                this.show(mediaData).catch(console.error);
            });
        });
    }

    // ========================================
    // MODAL DISPLAY
    // ========================================

    /**
     * Show media in modal
     * @param {Object} mediaData - Media data object
     * @param {string} mediaData.url - Media URL
     * @param {string} mediaData.type - Media type
     * @param {string} [mediaData.gallery] - Gallery ID
     * @returns {Promise<Modal>} Modal instance
     * @public
     */
    async show(mediaData) {
        // If part of gallery, set current gallery
        if (mediaData.gallery && this.galleries[mediaData.gallery]) {
            this.currentGallery = mediaData.gallery;
            this.currentIndex = 'galleryIndex' in mediaData ? mediaData.galleryIndex : 0;
        } else {
            this.currentGallery = null;
            this.currentIndex = 0;
        }

        // Generate unique modal ID
        const modalId = 'bm-media-' + Date.now();

        // Create modal HTML
        const modalHTML = this.generateModalHTML(modalId, mediaData);

        // Insert into DOM
        document.body.insertAdjacentHTML('beforeend', modalHTML);

        // Add media type class BEFORE showing modal
        const modal = document.getElementById(modalId);
        if (modal) {
            modal.classList.add(`bm-modal--${mediaData.type}`);

            if (this.config.debugMode) {
                console.log(`🎨 Added class BEFORE show: bm-modal--${mediaData.type}`);
            }
        }

        // Show modal
        try {
            await window.BaseModal.show(modalId, {
                theme: mediaData.theme || this.config.theme,
                animation: mediaData.animation || this.config.animation,
                size: mediaData.size || 'large',
                awaitCloseAnimation: true,
                // Disable swipe navigation to prevent conflicts with click events
                swipeNavigation: false,
                // Disable drag to dismiss for media modals (conflicts with navigation buttons)
                dragToDismiss: false,
                hapticFeedback: true,
                onClose: () => {
                    // Cleanup media resources
                    this.cleanupMedia(modalId);

                    // Clear any pending transitions
                    if (this.transitionTimeout) {
                        clearTimeout(this.transitionTimeout);
                        this.transitionTimeout = null;
                    }
                    this.isTransitioning = false;

                    // Remove keyboard listener
                    const modal = document.getElementById(modalId);
                    if (modal && modal._keyHandler) {
                        document.removeEventListener('keydown', modal._keyHandler);
                        if (this.config.debugMode) {
                            console.log('🧹 Cleaned up keyboard listener');
                        }
                    }

                    // Remove modal from DOM
                    setTimeout(() => {
                        if (modal) modal.remove();
                    }, 300);
                }
            });

            this.currentModal = modalId;

            // Add event delegation for navigation buttons
            if (modal) {
                // Click navigation
                modal.addEventListener('click', (e) => {
                    // Navigation buttons
                    const navButton = e.target.closest('[data-media-nav]');
                    if (navButton && !navButton.disabled) {
                        e.preventDefault();
                        e.stopPropagation();

                        const direction = navButton.dataset.mediaNav;

                        if (this.config.debugMode) {
                            console.log('📍 Navigation button clicked:', direction);
                        }

                        if (direction === 'next') {
                            this.nextMedia();
                        } else if (direction === 'prev') {
                            this.prevMedia();
                        }
                        return;
                    }

                    // Fullscreen button (only if enabled)
                    if (this.config.fullscreen) {
                        const fullscreenButton = e.target.closest('[data-media-fullscreen]');
                        if (fullscreenButton) {
                            e.preventDefault();
                            e.stopPropagation();

                            if (this.config.debugMode) {
                                console.log('🖥️ Fullscreen button clicked');
                            }

                            this.toggleFullscreen(modal);
                        }
                    }
                });

                // Keyboard navigation (only for galleries)
                if (this.currentGallery) {
                    const keyHandler = (e) => {
                        if (e.key === 'ArrowLeft') {
                            e.preventDefault();
                            if (this.config.debugMode) {
                                console.log('⌨️ Keyboard: ArrowLeft');
                            }
                            this.prevMedia();
                        } else if (e.key === 'ArrowRight') {
                            e.preventDefault();
                            if (this.config.debugMode) {
                                console.log('⌨️ Keyboard: ArrowRight');
                            }
                            this.nextMedia();
                        }
                    };

                    document.addEventListener('keydown', keyHandler);

                    // Cleanup keyboard listener on close
                    modal._keyHandler = keyHandler;
                }

                // Touch swipe on media wrapper (not entire modal)
                if (this.currentGallery) {
                    const wrapper = modal.querySelector('.bm-media-wrapper');
                    if (wrapper) {
                        let touchStartX = 0;
                        let touchStartY = 0;
                        let touchEndX = 0;
                        let touchEndY = 0;
                        const minSwipeDistance = 80; // Increased threshold

                        wrapper.addEventListener('touchstart', (e) => {
                            touchStartX = e.changedTouches[0].screenX;
                            touchStartY = e.changedTouches[0].screenY;
                        }, { passive: true });

                        wrapper.addEventListener('touchend', (e) => {
                            touchEndX = e.changedTouches[0].screenX;
                            touchEndY = e.changedTouches[0].screenY;

                            const deltaX = touchEndX - touchStartX;
                            const deltaY = touchEndY - touchStartY;

                            // Only if horizontal swipe is dominant
                            if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
                                if (this.config.debugMode) {
                                    console.log('👆 Swipe detected:', { deltaX, deltaY });
                                }

                                if (deltaX > 0) {
                                    // Swipe right → previous
                                    this.prevMedia();
                                } else {
                                    // Swipe left → next
                                    this.nextMedia();
                                }
                            }
                        }, { passive: true });
                    }
                }
            }

            // Handle video autoplay
            if (mediaData.type === 'video' && mediaData.autoplay) {
                const video = document.querySelector(`#${modalId} video`);
                if (video) {
                    video.play().catch(err => {
                        if (this.config.debugMode) {
                            console.log('Autoplay prevented:', err);
                        }
                    });
                }
            }

        } catch (error) {
            console.error('BaseModal.Media: Error showing modal:', error);
        }
    }

    /**
     * Update gallery content without closing modal
     * @param {Object} mediaData - New media data
     * @private
     */
    updateGallery(mediaData) {
        // If part of gallery, set current gallery
        if (mediaData.gallery && this.galleries[mediaData.gallery]) {
            this.currentGallery = mediaData.gallery;
            this.currentIndex = mediaData.galleryIndex;
        } else {
            this.currentGallery = null;
            this.currentIndex = 0;
        }

        // Update media content
        this.updateMedia().catch(console.error);
    }

    // ========================================
    // MEDIA RENDERING
    // ========================================

    /**
     * Render media content based on type
     * @param {Object} mediaData - Media data object
     * @returns {string} HTML string for media content
     * @private
     */
    renderMedia(mediaData) {
        switch (mediaData.type) {
            case 'image':
                return `<img src="${mediaData.url}"
                                 alt="${this.escapeHtml(mediaData.title)}"
                                 loading="${this.config.loading}"
                                 class="bm-media-image">`;

            case 'video':
                return `<video ${this.config.controls ? 'controls' : ''}
                                   ${mediaData.autoplay ? 'autoplay' : ''}
                                   ${mediaData.loop ? 'loop' : ''}
                                   preload="${this.config.preload}"
                                   class="bm-media-video">
                                <source src="${mediaData.url}" type="video/mp4">
                                Your browser does not support the video tag.
                            </video>`;

            case 'iframe':
                const iframeSrc = this.processIframeUrl(mediaData.url, mediaData);
                return `<iframe src="${iframeSrc}"
                                    width="${mediaData.iframeWidth}"
                                    height="${mediaData.iframeHeight}"
                                    allow="${mediaData.iframeAllow}"
                                    loading="${this.config.loading}"
                                    allowfullscreen
                                    class="bm-media-iframe"></iframe>`;

            default:
                return `<p>${this.config.locales.unsupported}: ${mediaData.type}</p>`;
        }
    }

    // ========================================
    // MODAL HTML GENERATION
    // ========================================

    /**
     * Generate modal HTML structure
     * @param {string} modalId - Unique modal ID
     * @param {Object} mediaData - Media data object
     * @returns {string} HTML string
     * @private
     */
    generateModalHTML(modalId, mediaData) {
        const hasGallery = this.currentGallery !== null;
        const galleryItems = hasGallery ? this.galleries[this.currentGallery] : [];
        const totalItems = galleryItems.length;

        return `
<div id="${modalId}" class="bm-modal bm-media-modal" aria-hidden="true">
    <div tabindex="-1" class="bm-modal__overlay">
        <div role="dialog" class="bm-modal__container bm-modal__container--media" aria-modal="true">
            <button aria-label="${this.config.locales.close}"
                    class="bm-modal__close"
                    data-basemodal-close>
                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
                    <path d='M18.5297 17.47C18.8227 17.763 18.8227 18.238 18.5297 18.531C18.3837 18.677 18.1917 18.751 17.9997 18.751C17.8077 18.751 17.6158 18.678 17.4698 18.531L11.9997 13.061L6.52975 18.531C6.38375 18.677 6.19175 18.751 5.99975 18.751C5.80775 18.751 5.61575 18.678 5.46975 18.531C5.17675 18.238 5.17675 17.763 5.46975 17.47L10.9398 12L5.46975 6.53005C5.17675 6.23705 5.17675 5.76202 5.46975 5.46902C5.76275 5.17602 6.23775 5.17602 6.53075 5.46902L12.0008 10.939L17.4707 5.46902C17.7637 5.17602 18.2387 5.17602 18.5317 5.46902C18.8247 5.76202 18.8247 6.23705 18.5317 6.53005L13.0617 12L18.5297 17.47Z'
                          fill='currentColor'/>
                </svg>
            </button>

            ${this.config.fullscreen ? `
            <button aria-label="${this.config.locales.fullscreen}"
                    class="bm-modal__fullscreen"
                    data-media-fullscreen>
                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' pointer-events='none'>
                    <path d='M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z'
                          fill='currentColor'/>
                </svg>
            </button>
            ` : ''}

            <div class="bm-modal__content bm-media-content">
                ${mediaData.title ? `<div class="bm-media-title">${this.escapeHtml(mediaData.title)}</div>` : ''}

                <div class="bm-media-wrapper">
                    ${this.renderMedia(mediaData)}
                </div>

                ${mediaData.description ? `<div class="bm-media-description">${this.escapeHtml(mediaData.description)}</div>` : ''}

                ${hasGallery ? `
                    <button class="bm-media-nav bm-media-nav__prev" type="button" aria-label="${this.config.locales.prev}" data-media-nav="prev" ${this.currentIndex === 0 ? 'disabled' : ''}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" pointer-events="none">
                            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <button class="bm-media-nav bm-media-nav__next" type="button" aria-label="${this.config.locales.next}" data-media-nav="next" ${this.currentIndex === totalItems - 1 ? 'disabled' : ''}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" pointer-events="none">
                            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <div class="bm-media-counter">
                        <span class="bm-media-counter__current">${this.currentIndex + 1}</span> / <span class="bm-media-counter__total">${totalItems}</span>
                    </div>

                ` : ''}
            </div>
        </div>
        <div class="bm-modal__bg" aria-hidden="true" data-basemodal-close></div>
    </div>
</div>`;
    }

    // ========================================
    // IFRAME PROCESSING
    // ========================================

    /**
     * Process iframe URL (add autoplay for videos)
     * @param {string} url - Original iframe URL
     * @param {Object} mediaData - Media data object
     * @returns {string} Processed iframe URL
     * @private
     */
    processIframeUrl(url, mediaData) {
        // YouTube
        if (PATTERNS.youtube.test(url)) {
            const match = url.match(PATTERNS.youtube);
            if (match && match[1]) {
                const videoId = match[1];
                const autoplay = mediaData.autoplay ? '&autoplay=1' : '';
                return `https://www.youtube.com/embed/${videoId}?${autoplay}`;
            }
        }

        // Vimeo
        if (PATTERNS.vimeo.test(url)) {
            const match = url.match(PATTERNS.vimeo);
            if (match && match[1]) {
                const videoId = match[1];
                const autoplay = mediaData.autoplay ? '&autoplay=1' : '';
                return `https://player.vimeo.com/video/${videoId}?${autoplay}`;
            }
        }

        // For VK Video, Rutube and others - use URL as is
        return url;
    }

    // ========================================
    // GALLERY NAVIGATION
    // ========================================

    /**
     * Navigate to next media in gallery
     * @public
     */
    nextMedia() {
        if (!this.currentGallery) return;

        const gallery = this.galleries[this.currentGallery];

        if (this.config.debugMode) {
            console.log('🔜 nextMedia:', {
                currentIndex: this.currentIndex,
                galleryLength: gallery.length,
                canGoNext: this.currentIndex < gallery.length - 1
            });
        }

        if (this.currentIndex < gallery.length - 1) {
            this.currentIndex++;

            if (this.config.debugMode) {
                console.log('✅ Moving to index:', this.currentIndex);
            }

            this.updateMedia('next').catch(console.error);
        }
    }

    /**
     * Navigate to previous media in gallery
     * @public
     */
    prevMedia() {
        if (!this.currentGallery) return;

        if (this.config.debugMode) {
            console.log('🔙 prevMedia:', {
                currentIndex: this.currentIndex,
                canGoPrev: this.currentIndex > 0
            });
        }

        if (this.currentIndex > 0) {
            this.currentIndex--;

            if (this.config.debugMode) {
                console.log('✅ Moving to index:', this.currentIndex);
            }

            this.updateMedia('prev').catch(console.error);
        }
    }

    /**
     * Update media content in current modal
     * Used for gallery navigation without closing modal
     * @param {string} direction - Navigation direction ('next' or 'prev')
     * @private
     */
    async updateMedia(direction = 'next') {
        if (!this.currentModal || !this.currentGallery) return;

        // Prevent multiple simultaneous transitions
        if (this.isTransitioning) {
            if (this.config.debugMode) {
                console.log('⏸️ Transition in progress, skipping...');
            }
            return;
        }

        this.isTransitioning = true;

        const gallery = this.galleries[this.currentGallery];
        const mediaData = gallery[this.currentIndex];

        if (this.config.debugMode) {
            console.log('🔄 updateMedia:', {
                currentIndex: this.currentIndex,
                mediaData: mediaData,
                gallery: this.currentGallery,
                isTransitioning: this.isTransitioning
            });
        }

        // Get modal elements
        const modal = document.getElementById(this.currentModal);
        if (!modal) return;

        const wrapper = modal.querySelector('.bm-media-wrapper');
        const title = modal.querySelector('.bm-media-title');
        const description = modal.querySelector('.bm-media-description');
        const counter = modal.querySelector('.bm-media-counter .bm-media-counter__current');
        const prevBtn = modal.querySelector('.bm-media-nav__prev');
        const nextBtn = modal.querySelector('.bm-media-nav__next');

        // Cleanup old media
        this.cleanupMedia(this.currentModal);

        // Update media type class on modal
        if (modal) {
            // Remove old media type classes
            modal.classList.remove('bm-modal--image', 'bm-modal--video', 'bm-modal--iframe');
            // Add new media type class
            modal.classList.add(`bm-modal--${mediaData.type}`);

            if (this.config.debugMode) {
                console.log(`🎨 Updated class: bm-modal--${mediaData.type}`);
            }
        }

        // Crossfade animation - prevents container collapse
        if (wrapper) {
            // Create new content element
            const newContent = document.createElement('div');
            newContent.className = 'bm-media-content-new';
            newContent.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; opacity: 0; transition: opacity 0.3s ease;';
            newContent.innerHTML = this.renderMedia(mediaData);

            // Add new content to wrapper
            wrapper.style.position = 'relative';
            wrapper.appendChild(newContent);

            // Wait for image to load (if it's an image)
            const newImage = newContent.querySelector('img');
            const startTransition = () => {
                // Fade out old content
                const oldContent = wrapper.querySelector('.bm-media-content-old, .bm-media-image, .bm-media-video, .bm-media-iframe');
                if (oldContent && oldContent !== newContent) {
                    oldContent.style.transition = 'opacity 0.3s ease';
                    oldContent.style.opacity = '0';
                }

                // Fade in new content
                requestAnimationFrame(() => {
                    newContent.style.opacity = '1';
                });

                // Remove old content after transition
                this.transitionTimeout = setTimeout(() => {
                    // Remove only old content (not the new one)
                    Array.from(wrapper.children).forEach(child => {
                        if (child !== newContent) {
                            child.remove();
                        }
                    });

                    // Unwrap new content (move its children to wrapper directly)
                    while (newContent.firstChild) {
                        wrapper.insertBefore(newContent.firstChild, newContent);
                    }

                    // Remove empty wrapper div
                    newContent.remove();

                    // Reset wrapper styles
                    wrapper.style.position = '';

                    // Reset transition flag
                    this.isTransitioning = false;

                    if (this.config.debugMode) {
                        console.log(`💫 Crossfade complete (${direction})`);
                    }
                }, 300);
            };

            // If you image, wait for load
            if (newImage) {
                if (newImage.complete) {
                    startTransition();
                } else {
                    newImage.onload = startTransition;
                    newImage.onerror = startTransition; // Start anyway on error
                }
            } else {
                // For video/iframe, start immediately
                startTransition();
            }

            if (this.config.debugMode) {
                console.log(`🔄 Starting crossfade (${direction})`);
            }
        }

        if (title) {
            title.textContent = mediaData.title || '';
            title.style.display = mediaData.title ? 'block' : 'none';
        }

        if (description) {
            description.textContent = mediaData.description || '';
            description.style.display = mediaData.description ? 'block' : 'none';
        }

        if (counter) {
            counter.textContent = this.currentIndex + 1;
        }

        // Update navigation buttons
        if (prevBtn) {
            prevBtn.disabled = this.currentIndex === 0;
        }

        if (nextBtn) {
            nextBtn.disabled = this.currentIndex === gallery.length - 1;
        }

        // Autoplay video if needed
        if (mediaData.type === 'video' && mediaData.autoplay) {
            const video = wrapper.querySelector('video');
            if (video) {
                video.play().catch(err => {
                    if (this.config.debugMode) {
                        console.log('Autoplay prevented:', err);
                    }
                });
            }
        }
    }

    // ========================================
    // UTILITIES
    // ========================================

    /**
     * Toggle fullscreen mode for modal
     * Uses native Fullscreen API
     * @param {HTMLElement} modal - Modal element
     * @public
     */
    toggleFullscreen(modal) {
        if (!modal) return;

        if (!document.fullscreenElement) {
            // Enter fullscreen
            modal.requestFullscreen().then(() => {
                modal.classList.add('bm-modal--fullscreen');

                if (this.config.debugMode) {
                    console.log('✅ Entered fullscreen mode');
                    console.log('🎨 Added class: bm-modal--fullscreen');
                }
            }).catch(err => {
                console.error('Error entering fullscreen:', err);
            });
        } else {
            // Exit fullscreen
            document.exitFullscreen().then(() => {
                modal.classList.remove('bm-modal--fullscreen');

                if (this.config.debugMode) {
                    console.log('✅ Exited fullscreen mode');
                    console.log('🎨 Removed class: bm-modal--fullscreen');
                }
            }).catch(err => {
                console.error('Error exiting fullscreen:', err);
            });
        }
    }

    /**
     * Cleanup media resources
     * Stops videos and clears iframes to prevent memory leaks
     * @param {string} modalId - Modal ID to clean up
     * @private
     */
    cleanupMedia(modalId) {
        const modal = document.getElementById(modalId);
        if (!modal) return;

        // Stop videos
        const videos = modal.querySelectorAll('video');
        videos.forEach(video => {
            video.pause();
            video.currentTime = 0;
        });

        // Clear iframes
        const iframes = modal.querySelectorAll('iframe');
        iframes.forEach(iframe => {
            iframe.src = '';
        });
    }

    /**
     * Escape HTML to prevent XSS attacks
     * @param {string} text - Text to escape
     * @returns {string} Escaped HTML
     * @private
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Destroy media modal instance
     * Cleans up all references and galleries
     * @public
     */
    destroy() {
        this.mediaElements = [];
        this.galleries = {};
        this.currentGallery = null;
        this.currentIndex = 0;
        this.currentModal = null;
    }
}

// ========================================
// EXPORT
// ========================================

/**
 * Export BaseModalMedia class as default
 * @module BaseModalMedia
 */
export default BaseModalMedia;
